import { AlertCircle, ArrowLeft, ArrowRight, Check, Layers } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ApiError, sourcesApi, type GovernedSource } from "../../services/api";
import { isDataDomain } from "../../services/dataDomain";
import { devTrace } from "../../services/devTrace";
import type { SelectedContext } from "../../services/storage";
import {
  PROJECT_TYPES,
  UNASSIGNED_ID,
  categoryOf,
  projectTypeFor,
  projectTypeLabel,
  type ProjectTypeDefinition,
} from "./assistants";

/**
 * Where a session starts: a project type, then the assistant within it.
 *
 * Two choices rather than one, because a project type is a reporting category
 * and a category can hold several published assistants — a service operation
 * covering two warehouses is ordinary. Landing straight in the chat left the
 * second choice implicit, so a question could be answered from whichever
 * context happened to be pinned last.
 *
 * Categories are matched strictly: an assistant appears under one project type
 * and no other. A category with nothing published is shown and disabled rather
 * than hidden, because "nothing has been published for Executive Reporting yet"
 * is a fact worth seeing, and a missing card reads as a bug.
 */
interface AssistantsPageProps {
  selectedContext: SelectedContext | null;
  selectedAssistant: string | null;
  onChoose: (projectType: string, context: SelectedContext) => void;
}

export function AssistantsPage({
  selectedContext,
  selectedAssistant,
  onChoose,
}: AssistantsPageProps) {
  const [sources, setSources] = useState<GovernedSource[] | null>(null);
  const [loadError, setLoadError] = useState("");
  // Local to this page: the category being browsed. It only becomes the
  // session's choice once an assistant is picked, so backing out of a
  // half-made selection changes nothing.
  const [browsing, setBrowsing] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    sourcesApi
      .list()
      .then((items) => {
        if (!cancelled) setSources(items);
      })
      .catch((error) => {
        if (cancelled) return;
        setSources([]);
        setLoadError(
          error instanceof ApiError
            ? error.message
            : "The context layer could not be reached.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Assistants per category. Strict: nothing appears under two categories. */
  const byCategory = useMemo(() => {
    const grouped = new Map<string, GovernedSource[]>();
    for (const type of PROJECT_TYPES) grouped.set(type.id, []);
    for (const source of sources ?? []) {
      grouped.get(categoryOf(source.dataDomain))?.push(source);
    }
    return grouped;
  }, [sources]);

  function choose(projectType: string, source: GovernedSource) {
    const context: SelectedContext = {
      contextId: source.id,
      contextVersion: source.contextVersion,
      name: source.name,
      // Carried so the analyst is named correctly on the first render, rather
      // than waiting for the source list to resolve the domain again.
      dataDomain: isDataDomain(source.dataDomain) ? source.dataDomain : null,
    };
    onChoose(projectType, context);
    devTrace.record({
      runId: crypto.randomUUID(),
      step: "context_selected",
      actor: "Assistant landing",
      level: "success",
      summary: `${projectTypeFor(projectType)?.label ?? projectType} pinned to `
        + `${source.name} ${source.contextVersion}.`,
      detail: {
        projectType,
        contextId: source.id,
        contextVersion: source.contextVersion,
      },
    });
  }

  const active = projectTypeFor(browsing);
  if (active) {
    return (
      <AssistantList
        projectType={active}
        assistants={byCategory.get(active.id) ?? []}
        selectedContext={selectedContext}
        onBack={() => setBrowsing(null)}
        onChoose={(source) => choose(active.id, source)}
      />
    );
  }

  return (
    <div className="content-stack">
      <section className="hero compact-hero">
        <div>
          <h1>Which assistant?</h1>
          <p>
            Select the assistant that answers from its certified semantic
            model.
          </p>
        </div>
      </section>

      {loadError && (
        <div className="form-error" role="alert">
          <AlertCircle size={17} aria-hidden="true" />
          {loadError}
        </div>
      )}

      <div className="assistant-grid">
        {PROJECT_TYPES.map((type) => {
          const assistants = byCategory.get(type.id) ?? [];
          // Hidden only when it holds nothing and never could: an empty
          // Unassigned bucket is an absence of legacy data, not a category.
          if (type.id === UNASSIGNED_ID && assistants.length === 0) return null;
          const loading = sources === null;
          const empty = !loading && assistants.length === 0;
          return (
            <button
              type="button"
              key={type.id}
              className={
                selectedAssistant === type.id
                  ? "assistant-card current"
                  : "assistant-card"
              }
              disabled={loading || empty}
              onClick={() => setBrowsing(type.id)}
            >
              <span className="assistant-mark" aria-hidden="true">
                <Layers size={20} />
              </span>
              <strong>{type.label}</strong>
              <p>{type.summary}</p>
              <span className="assistant-meta">
                {loading
                  ? "Checking published assistants…"
                  : empty
                    ? "No assistant published yet"
                    : `${assistants.length} assistant${assistants.length === 1 ? "" : "s"}`}
                {!loading && !empty && <ArrowRight size={14} aria-hidden="true" />}
              </span>
              {selectedAssistant === type.id && (
                <span className="status-badge certified">
                  <Check size={14} aria-hidden="true" /> Current
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface AssistantListProps {
  projectType: ProjectTypeDefinition;
  assistants: GovernedSource[];
  selectedContext: SelectedContext | null;
  onBack: () => void;
  onChoose: (source: GovernedSource) => void;
}

/**
 * The same table the Semantic Model Inventory draws, with the same columns.
 *
 * Cards read well for three categories and badly for a list of models: the
 * figures a reader compares — entities, metrics, rules, version, readiness —
 * are what decide which assistant to use, and they only line up in a table.
 * Choosing is the same radio the inventory uses, so the two pages behave alike.
 */
function AssistantList({
  projectType,
  assistants,
  selectedContext,
  onBack,
  onChoose,
}: AssistantListProps) {
  const ready = assistants.filter((item) => item.queryable);
  const totalMetrics = ready.reduce((total, item) => total + item.metricCount, 0);
  // The model awaiting confirmation. Its radio shows as chosen while the
  // dialog is open; Cancel clears it, so the pinned choice reappears.
  const [pending, setPending] = useState<GovernedSource | null>(null);
  const checkedId = pending?.id ?? selectedContext?.contextId;

  function confirm() {
    if (!pending) return;
    const chosen = pending;
    setPending(null);
    onChoose(chosen);
  }

  return (
    <div className="content-stack">
      <section className="hero compact-hero">
        <div>
          <button
            type="button"
            className="button tertiary assistant-back"
            onClick={onBack}
          >
            <ArrowLeft size={15} aria-hidden="true" /> All project types
          </button>
          <h1>{projectType.label}</h1>
          <p>
            Choose the assistant for this work. Every figure it reports comes
            from the certified semantic model behind it.
          </p>
        </div>
        <div className="hero-stats">
          <div>
            <strong>{ready.length}</strong>
            <small>Assistants ready</small>
          </div>
          <div>
            <strong>{totalMetrics}</strong>
            <small>Certified metrics</small>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <h2>Assistants in {projectType.label}</h2>
            <p>
              Each one answers from the certified semantic model beside it,
              reported by the Business Context Layer.
            </p>
          </div>
          {selectedContext && (
            <span className="status-badge certified">
              <Check size={15} aria-hidden="true" />
              {selectedContext.name} {selectedContext.contextVersion}
            </span>
          )}
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Use</th>
              <th scope="col">Semantic model</th>
              <th scope="col">Created by</th>
              <th scope="col">Project type</th>
              <th scope="col">Description</th>
              <th scope="col">Source system</th>
              <th scope="col">Version</th>
              <th scope="col" className="numeric">Entities</th>
              <th scope="col" className="numeric">Metrics</th>
              <th scope="col" className="numeric">Rules</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {assistants.map((assistant) => (
              <tr key={assistant.id}>
                <td>
                  <input
                    type="radio"
                    name="assistant-choice"
                    value={assistant.id}
                    checked={checkedId === assistant.id}
                    // No runtime means it can answer nothing, so it is listed
                    // for transparency but cannot be chosen.
                    disabled={!assistant.queryable}
                    onChange={() => setPending(assistant)}
                    aria-label={`Use ${assistant.name}`}
                  />
                </td>
                <td>
                  <strong>{assistant.name}</strong>
                  <small className="muted"> {assistant.id}</small>
                </td>
                <td>
                  {assistant.createdBy || (
                    <span className="muted">Unattributed</span>
                  )}
                </td>
                <td>
                  {assistant.dataDomain ? (
                    projectTypeLabel(assistant.dataDomain)
                  ) : (
                    <span className="muted">Unassigned</span>
                  )}
                </td>
                <td>
                  {assistant.description || <span className="muted">—</span>}
                </td>
                <td>
                  {assistant.sourceSystem || (
                    <span className="muted">unknown</span>
                  )}
                  {assistant.catalogs.length > 0 && (
                    <small className="muted">
                      {" "}
                      {assistant.catalogs.join(", ")}
                      {assistant.schemas.length > 0
                        ? ` · ${assistant.schemas.join(", ")}`
                        : ""}
                    </small>
                  )}
                </td>
                <td>
                  {assistant.contextVersion}
                  {assistant.availableVersions.length > 1 && (
                    <small className="muted">
                      {" "}
                      of {assistant.availableVersions.length}
                    </small>
                  )}
                </td>
                <td className="numeric">{assistant.entityCount}</td>
                <td className="numeric">{assistant.metricCount}</td>
                <td className="numeric">{assistant.ruleCount}</td>
                <td>
                  <span
                    className={`status-pill ${
                      assistant.status === "connected" ? "ready" : "warn"
                    }`}
                  >
                    {assistant.status === "connected"
                      ? "Connected"
                      : assistant.queryable
                        ? "Unavailable"
                        : "Not queryable"}
                  </span>
                  {!assistant.queryable && (
                    <small className="muted"> no runtime bound</small>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {pending && (
        <ConfirmDialog
          title="Ready to start your analysis"
          onConfirm={confirm}
          onCancel={() => setPending(null)}
        >
          <p
            className="confirm-lead"
            title={`You selected the ${pending.name} semantic model${
              pending.contextVersion ? ` (version ${pending.contextVersion})` : ""
            }.`}
          >
            You selected the <strong>{pending.name}</strong> semantic model
            {pending.contextVersion ? ` (version ${pending.contextVersion})` : ""}.
          </p>
          <p className="confirm-wrap">
            Every answer and number will come from its certified metrics. Select
            OK to start analysing your data, or Cancel to choose a different
            model.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
